import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog as HeadlessDialog } from '@headlessui/react';
import cx from 'classnames';

import useIsMobile from '~/hooks/useIsMobile';

import { POPOVER_PANEL_CLASSES } from './_styles/popover';
import { BottomSheetDialog } from './BottomSheetDialog';
import { Dialog } from './Dialog';
import { SolidButton } from './SolidButton';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDestructive?: boolean;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  body?: ReactNode;
  afterLeave?: () => void;
}

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
  isDestructive = false,
  isLoading = false,
  confirmDisabled = false,
  body,
  afterLeave,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const content = (
    <>
      <HeadlessDialog.Title className="text-lg font-semibold text-black">{title}</HeadlessDialog.Title>
      <HeadlessDialog.Description className="mt-2 text-base leading-6 text-black/60">
        {description}
      </HeadlessDialog.Description>
      {body}
      <div className="mt-6 flex flex-col gap-3">
        <SolidButton
          variant={isDestructive ? 'destructive' : 'primary'}
          onClick={onConfirm}
          disabled={isLoading || confirmDisabled}
        >
          {isLoading ? '...' : confirmLabel}
        </SolidButton>
        <SolidButton variant="subtle" onClick={onCancel} disabled={isLoading}>
          {t('community.action_menu.cancel', 'Cancel')}
        </SolidButton>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <BottomSheetDialog isOpen={isOpen} onClose={onCancel} panelClassName="px-5 pt-6" afterLeave={afterLeave}>
        {content}
        <div className="h-4" />
      </BottomSheetDialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      afterLeave={afterLeave}
      className={cx(POPOVER_PANEL_CLASSES, 'w-full max-w-sm px-6 pt-6 pb-6 border-0')}
    >
      {content}
    </Dialog>
  );
};
